import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { AuthSession } from "@supabase/supabase-js";
import { getFromLocalStorage, LocalStorageKey } from "@ponder-shared/utils/localStorage";
import { RoleAndPermissions } from "./role-permissions";

/**
 * Handles local storage for auth credentials.
 */
@Injectable({ providedIn: "root" })
export class AuthStorage {
    public static populateFromSb(): void {
        AuthStorage.clearStorage();

        var sbSessionStr = getFromLocalStorage(`sb-${environment.supabase.id}-auth-token`);

        if (!sbSessionStr) {
            return;
        }

        var sbSession = JSON.parse(sbSessionStr) as AuthSession;

        AuthStorage.setToken(sbSession.access_token);
        AuthStorage.setSessionId(sbSession.user.id);
        AuthStorage.setUsername(sbSession.user.user_metadata["username"]);
        AuthStorage.setEmail(sbSession.user.email!);
    }

    public static getToken(): string | undefined {
        return getFromLocalStorage(LocalStorageKey.AuthToken);
    }

    private static setToken(token: string): void {
        localStorage.setItem(LocalStorageKey.AuthToken, token);
    }

    public static getSessionId(): string | undefined {
        return getFromLocalStorage(LocalStorageKey.SessionId);
    }

    private static setSessionId(sessionId: string): void {
        localStorage.setItem(LocalStorageKey.SessionId, sessionId);
    }

    public static getUsername(): string | undefined {
        return getFromLocalStorage(LocalStorageKey.AuthUsername);
    }

    private static setUsername(username: string): void {
        localStorage.setItem(LocalStorageKey.AuthUsername, username);
    }

    public static getDisplayName(): string | undefined {
        return getFromLocalStorage(LocalStorageKey.DisplayName);
    }

    private static setEmail(email: string): void {
        localStorage.setItem(LocalStorageKey.EmailAddress, email);
    }

    public static getEmail(): string | undefined {
        return getFromLocalStorage(LocalStorageKey.EmailAddress);
    }

    public static setDisplayName(displayName?: string): void {
        if (displayName) {
            localStorage.setItem(LocalStorageKey.DisplayName, displayName);
        } else {
            localStorage.removeItem(LocalStorageKey.DisplayName);
        }
    }
    
    public static setRoleAndPermissions(rolePermissions: RoleAndPermissions): void {
        if (!rolePermissions.isExpired() && rolePermissions.role && rolePermissions.permissions) {
            localStorage.setItem(LocalStorageKey.RoleAndPermissions, JSON.stringify(rolePermissions));
        }
    }

    public static getRolePermissions(): RoleAndPermissions | undefined {
        var storedJson = getFromLocalStorage(LocalStorageKey.RoleAndPermissions);

        if (storedJson) {
            var storedRolePermissions = JSON.parse(storedJson) as RoleAndPermissions;
            var rolePermissions = new RoleAndPermissions(storedRolePermissions.role, storedRolePermissions.permissions, storedRolePermissions.limits, storedRolePermissions.expires);

            if (!rolePermissions.isExpired()) {
                return rolePermissions;
            } else {
                localStorage.removeItem(LocalStorageKey.RoleAndPermissions);
            }
        }

        return undefined;
    }

    public static setTempSessionFlag(): void {
        localStorage.setItem(LocalStorageKey.AuthTempSession, "true");
    }

    public static isTempSession(): boolean {
        var flag = getFromLocalStorage(LocalStorageKey.AuthTempSession);

        if (flag) {
            return true;
        } else {
            return false;
        }
    }

    public static clearTempSessionFlag(): void {
        localStorage.removeItem(LocalStorageKey.AuthTempSession);
    }

    public static clearStorage(): void {
        localStorage.removeItem(LocalStorageKey.AuthToken);
        localStorage.removeItem(LocalStorageKey.AuthUsername);
        localStorage.removeItem(LocalStorageKey.AuthTempSession);
        localStorage.removeItem(LocalStorageKey.SessionId);
        localStorage.removeItem(LocalStorageKey.EmailAddress);
        localStorage.removeItem(LocalStorageKey.DisplayName);
        localStorage.removeItem(LocalStorageKey.RoleAndPermissions);
    }
}